import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import _ from "lodash";
import { useNavigate } from "react-router";
import { Input, Label } from "../../../components/shared/InputField";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../../components/utils";
import { Select } from "../../../components/shared/Select";
import {
  CompensationModellingData,
  CompanyData,
  HiringPlanData,
} from "../companyDetails/utils/OrgStructureJSONDataProc";
import { useGetOrgStructureV2 } from "../../../queries/organisationStructure";
import { useOrgTemplateStore } from "../../../store/useOrgTemplateStore";
import CompensationModellingCreation from "./CompensationModelling";
import { useGetAllCompensationModelling } from "../../../queries/compensationModelling";
import { useGetAllHiringPlans } from "../../../queries/hiringPlan";

type ExistingModels = {
  id: string | undefined;
  modelName: string;
};

const CreateCompensationModelling: React.FC = () => {
  const navigate = useNavigate();
  const [clickedNext, setClickedNext] = useState(false);
  const [prevData, setPrevData] = useState<CompanyData>();
  const { data: orgData, isFetching } = useGetOrgStructureV2();
  const { data: existingPlans, isFetching: plansFetching } =
    useGetAllCompensationModelling();
  const { data: _hiringPlans, isFetching: hiringPlansFetching } =
    useGetAllHiringPlans();
  const [hiringPlans, setHiringPlans] = useState<ExistingModels[]>([]);
  useEffect(() => {
    if (_hiringPlans) {
      setHiringPlans(
        _hiringPlans.map((v) => ({
          id: v.id,
          modelName: v.modelName,
        }))
      );
    }
  }, [hiringPlansFetching]);
  const {
    setSelectedCompanyData,
    selectedCompanyData,
    setCompensationModellingData,
  } = useOrgTemplateStore();
  const [newModel, setNewModel] = useState(true);
  const [selected, setSelected] = useState<boolean>(false);
  const [existingModels, setExistingModels] = useState<ExistingModels[]>([]);
  useEffect(() => {
    if (existingPlans) {
      setExistingModels(
        existingPlans.map((plan) => ({
          id: plan.id,
          modelName: plan.modelName,
        }))
      );
    }
  }, [plansFetching]);
  const initialModel: CompensationModellingData = {
    modelName: "",
    allocationMethod: "departmentWise",
    allocationType: "New Allocation",
    description: "",
    data: selectedCompanyData,
    selectedHiringPlan: false,
  };
  const [modelData, setModelData] =
    useState<CompensationModellingData>(initialModel);
  useEffect(() => {
    if (
      orgData &&
      _.isEmpty(prevData) &&
      JSON.stringify(prevData) !== JSON.stringify(orgData)
    ) {
      setPrevData(orgData);
      setSelectedCompanyData(orgData as CompanyData);
    }
  }, [isFetching]);
  function onPageChange() {
    if (!modelData || !modelData.modelName || modelData.modelName === "") {
      toast.error("Please Provide Model Name");
      return;
    }
    if (
      !modelData ||
      !modelData.allocationMethod ||
      modelData.allocationMethod === ""
    ) {
      toast.error("Please select Allocation Type");
      return;
    }
    setCompensationModellingData(modelData);
    setClickedNext(true);
  }
  return (
    <div>
      {!clickedNext ? (
        <VStack className="gap-6">
          <VStack className="px-4 bg-white rounded-lg shadow-custom">
            <Box className="px-4 py-4 ">
              <p className="text-xl font-semibold text-gray-dark">
                Compensation Modelling
              </p>
              <Box className="text-xs font-normal text-gray-light">
                Build multiple compensation models and make informed decisions
                on structuring the equity compensation of employees across
                various departments and grades.
              </Box>
            </Box>
            <VStack className="space-y-4">
              <VStack className="gap-4 p-4 ">
                <ButtonPrimary
                  onClick={() => {
                    setNewModel(true);
                    setSelected(true);
                    setModelData(initialModel);
                  }}
                  className="w-40 bg-gray-300 cursor-not-allowed hover:bg-gray-300"
                >
                  Model a Compensation
                </ButtonPrimary>
                <VStack className="py-4">
                  <HStack>
                    <HStack className=" h-0.5 w-14  bg-gray-201 rounded flex-col items-center mt-3 "></HStack>
                    <HStack className="px-2 text-base text-gray-950">Or</HStack>
                    <HStack className=" h-0.5 w-14  bg-gray-201 rounded flex-col items-center mt-3"></HStack>
                  </HStack>
                </VStack>
              </VStack>
              <HStack className="w-56 px-4 pb-4">
                <VStack>
                  <Label className="text-sm font-semibold">
                    Select a Model
                  </Label>
                  <HStack className="justify-center gap-4 align-middle">
                    <Select
                      className=""
                      options={existingModels}
                      valueGetter={(e) => e.id}
                      textGetter={(e) => e.modelName}
                      value={modelData.id || ""}
                      onChange={(e) => {
                        setNewModel(e.target.value === "");
                        setSelected(true);
                        setModelData(
                          (existingPlans?.find(
                            (plan) => plan.id === e.target.value
                          ) as CompensationModellingData) || initialModel
                        );
                      }}
                    />
                    <div className="align-center">
                      {" "}
                      <Icon
                        icon="ic:outline-edit"
                        height={24}
                        width={24}
                        className="mt-2"
                      ></Icon>
                    </div>
                  </HStack>
                </VStack>
              </HStack>
            </VStack>
          </VStack>

          {selected && (
            <VStack className="gap-3 px-4 pt-4 bg-white rounded-lg shadow-custom">
              <Box className="px-4 py-4 ">
                <p className="text-lg font-semibold text-gray-dark">
                  {newModel ? "New Compensation Model" : "Compensation Model"}
                </p>
              </Box>
              <HStack className="justify-between w-2/4 px-4 pt-2">
                <Box className="flex items-center ">
                  <Label className="flex items-center text-sm font-medium ">
                    Model Name
                  </Label>
                </Box>
                <Box className="w-2/4">
                  <Input
                    type="text"
                    disabled={!newModel}
                    value={modelData.modelName}
                    onChange={(e) => {
                      setModelData((prev) => ({
                        ...prev,
                        modelName: e.target.value,
                      }));
                    }}
                  />
                </Box>
              </HStack>
              <HStack className="justify-between w-2/4 px-4">
                <Box className="flex items-center ">
                  <Label className="flex items-center text-sm font-medium ">
                    Allocation Type
                  </Label>
                </Box>
                <Box className="w-2/4">
                  <Select
                    disabled={true}
                    options={[
                      "New Allocation",
                      "Refresh Options",
                      "Performance Grant",
                    ]}
                    value={modelData.allocationType || ""}
                    onChange={(e) => {
                      setModelData((prev) => ({
                        ...prev,
                        allocationType: e.target.value,
                      }));
                    }}
                  />
                </Box>
              </HStack>
              <HStack className="justify-between w-2/4 px-4">
                <Box className="flex items-center ">
                  <Label className="flex items-center text-sm font-medium ">
                    Description
                  </Label>
                </Box>
                <Box className="w-2/4">
                  <Input
                    type="text"
                    disabled={!newModel}
                    value={modelData.description}
                    onChange={(e) => {
                      setModelData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }));
                    }}
                  />
                </Box>
              </HStack>
              {newModel && (
                <HStack className="justify-between w-2/4 px-4">
                  <Box className="flex items-center ">
                    <Label className="flex items-center text-sm font-medium ">
                      Hiring Plan
                    </Label>
                  </Box>
                  <Box className="w-2/4">
                    <Select
                      options={hiringPlans}
                      textGetter={(e) => e.modelName}
                      valueGetter={(e) => e.id}
                      onChange={(e) => {
                        const plan = _hiringPlans?.find(
                          (p) => p.id === e.target.value
                        );
                        setModelData((prev) => ({
                          ...prev,
                          selectedHiringPlan: !!plan,
                          data: plan?.data || initialModel?.data,
                        }));
                      }}
                    />
                  </Box>
                </HStack>
              )}
              <HStack className="justify-end gap-4 p-4 row-reverse">
                <ButtonPrimary1
                  className="border-orange-501"
                  onClick={() => {
                    navigate(`/modelling/home`);
                  }}
                >
                  Cancel
                </ButtonPrimary1>
                <ButtonPrimary
                  className="min-w-max "
                  onClick={() => onPageChange()}
                >
                  Next
                </ButtonPrimary>
              </HStack>
            </VStack>
          )}
        </VStack>
      ) : (
        <VStack>
          <CompensationModellingCreation />
        </VStack>
      )}
    </div>
  );
};

export default CreateCompensationModelling;
